/**
 * What: The one frame every Study Builder section is drawn in: header (title, description, badge, one optional action), body (what the
 *   merchant edits), status area (live results from the Worker: counts, errors, actions, the working cue) and footer
 *   (small print, drawn as a footnote: an info icon in a left column, the content in a right column; a section that waits for
 *   a product also puts its one "choose a product" line here).
 * Transport / data-flow: Pure presentation. Callers decide what goes in each region; the frame owns layout only, so
 *   every section looks the same and gets its spacing and footnote layout for free.
 */
import type { BuilderHeadingSize } from '@testfeed/shopify-contracts'
import type { ReactNode } from 'react'
import { RequiredMark } from './RequiredMark'
import './SectionFrame.css'

/**
 * The one heading in the Study Builder. SectionFrame owns `s-heading` (ratchet `no-own-section-frame-in-builder-ui` keeps it
 * out of every other builder component), so a payload-driven heading — a section title here, or a `note` with
 * `emphasis: 'heading'` — renders through this single owner instead of a literal `<s-heading>` scattered across the renderer.
 * `size` is the Worker's closed heading size (the summary sidebar's headings); absent is the default.
 */
export function BlockHeading({ size, children }: { size?: BuilderHeadingSize; children: ReactNode }) {
  return <s-heading fontSize={size}>{children}</s-heading>
}

export type SectionFrameProps = {
  title: string
  /** The title's heading size (the summary sidebar's top heading); omitted for the default section-title size. */
  size?: BuilderHeadingSize
  description: string
  badge: { text: string; tone: 'info' | 'success' | 'warning' | 'critical' } | null
  /** Set when the whole section is a required choice: the "*" beside the title, with its hover text. `key` names the hover target. */
  required?: { key: string; reason: string }
  /** One button for the whole section, on the right of the header. Omitted when there is none. */
  action?: ReactNode
  /** Live results and the working cue. Omitted when there is nothing to say. */
  status?: ReactNode
  /** Small print, and the one line of a section that waits for a product. Omitted when there is none. */
  footer?: ReactNode
  children: ReactNode
}

export function SectionFrame({
  title,
  size,
  description,
  badge,
  required,
  action,
  status,
  footer,
  children,
}: SectionFrameProps) {
  return (
    <s-section>
      <s-stack direction="block" gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="base">
          <s-stack direction="block" gap="small-100">
            <s-stack direction="inline" alignItems="center" gap="small-200">
              <BlockHeading size={size}>{title}</BlockHeading>
              {required ? <RequiredMark markKey={required.key} reason={required.reason} /> : null}
              {badge ? <s-badge tone={badge.tone}>{badge.text}</s-badge> : null}
            </s-stack>
            {description ? <s-text color="subdued">{description}</s-text> : null}
          </s-stack>
          {action}
        </s-stack>
        {children}
        {status ? (
          <s-stack direction="block" gap="small-200">
            {status}
          </s-stack>
        ) : null}
        {footer ? (
          <div className="tf-section-footnote">
            <s-grid gridTemplateColumns="auto 1fr" gap="small-200" alignItems="start">
              <s-icon type="info" size="small" color="subdued" />
              <s-stack direction="block" gap="small-300">
                {footer}
              </s-stack>
            </s-grid>
          </div>
        ) : null}
      </s-stack>
    </s-section>
  )
}
